import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import {
  fetchTemplatesThunk,
  overwriteTemplateThunk,
} from "../../../../redux/reducers/ui/uiThunks";
import {
  getTemplates,
  getIsTemplatesLoading,
  getTemplatesError,
} from "../../../../redux/reducers/ui/uiSelectors";
import { getTemplatesCount } from "../../../../redux/reducers/ui/selectors/templateSelectors";
import { useURLRouting } from "../../../../utils/hooks/useURLRouting";
import { ITemplate } from "../../../../redux/reducers/ui/uiTypes";
import styles from "./TemplateItemList.module.scss";
import { CloudIcon } from "../../../../assets/svg/CloudIcon";
import { DeleteIcon } from "../../../../assets/svg/DeleteIcon";
import { ExtraActions } from "../../../../assets/svg/ExtraActions";
import { deleteTemplateThunk } from "../../../../redux/reducers/ui/uiThunks";
import { restoreTemplateThunk } from "../../../../redux/reducers/ui/uiThunks";
import { popUp } from "../../../../features/popUp/PopUp";
import { DropdownButton } from "../../../../shared/DropdownButton/DropdownButton";
import { ExtraActionsMenu } from "../../../../shared/ExtraActions/ExtraActionsMenu";
import { RepeatIcon } from "../../../../assets/svg/RepeatIcon";
import { getConfigInfo } from "../../../../utils/functions/playcanvas/getConfigInfo";
import { useStore } from "react-redux";
import { getDataForTemplateConfig } from "../../../../redux/reducers/ui/selectors/templateSelectors";
import { RootState } from "../../../../redux/store";
import { changeShowModal } from "../../../../redux/reducers/app/appReducer";
import { UrlService } from "../../../../services/UrlService";

interface TemplateItemListProps {
  page: number;
}

export const TemplateItemList = ({ page }: TemplateItemListProps) => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const { templateID } = useURLRouting();
  const folderId = templateID ? Number(templateID) : null;
  const [rewritingId, setRewritingId] = useState<number | null>(null);

  const templates = useAppSelector(getTemplates());
  const templatesCount = useAppSelector(getTemplatesCount());
  const isLoading = useAppSelector(getIsTemplatesLoading());
  const error = useAppSelector(getTemplatesError());

  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  const getTemplatesList = async () => {
    if (folderId !== null) {
      await dispatch(fetchTemplatesThunk({ folderId, page })).unwrap();
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await dispatch(deleteTemplateThunk(id)).unwrap();
      popUp({ type: "success", message: "Template deleted" });
    } catch (e: any) {
      popUp({ type: "failure", message: e.message || "Delete failed" });
    }
  };

  // Restore Template
  const handleRestore = async (id: number) => {
    try {
      dispatch(changeShowModal({ nameModal: "loader", stateModal: true }));

      await dispatch(restoreTemplateThunk(id)).unwrap();

      dispatch(changeShowModal({ nameModal: "loader", stateModal: false }));
      popUp({ type: "success", message: "Template restored" });
    } catch (e: any) {
      dispatch(changeShowModal({ nameModal: "loader", stateModal: false }));
      popUp({ type: "failure", message: e.message || "Restore failed" });
    }
  };

  const handleRewrite = async (id: number) => {
    try {
      // prevent concurrent rewrites
      if (rewritingId !== null) return;

      setRewritingId(id);

      // Prepare config for overwrite (raw JSON payload)
      const raw = await getConfigInfo();
      const config = getDataForTemplateConfig(raw)(store.getState());

      console.log("config config", config);

      await dispatch(
        overwriteTemplateThunk({
          id,
          config,
        })
      ).unwrap();

      popUp({ type: "success", message: "Template updated" });
    } catch (e: any) {
      popUp({ type: "failure", message: e.message || "Rewrite failed" });
    } finally {
      setRewritingId(null);
    }
  };

  useEffect(() => {
    if (folderId !== null) {
      getTemplatesList();
    }
  }, [folderId]);

  useEffect(() => {
    if (folderId !== null) {
      getTemplatesList();
    }
  }, [page]);

  return (
    <div className={styles.content} id="template-item-header">
      <div className={styles.templates_grid}>
        {isLoading && <p>Loading templates...</p>}
        {error && <p className={styles.error}>{error}</p>}
        {!isLoading &&
          !error &&
          (templates && templates.length > 0 ? (
            <>
              {(templates as ITemplate[]).map(template => (
                <div key={template.id} className={styles.item}>
                  <div className={styles.img_wrapper}>
                    {template.image ? (
                      <img src={UrlService.getImageServerCdnUrl(template.image)} alt={template.name} />
                    ) : (
                      <CloudIcon />
                    )}
                    {rewritingId === template.id && (
                      <div className={styles.loaderOverlay}>
                        <div className={styles.loader} />
                      </div>
                    )}
                  </div>
                  <div className={styles.information}>
                    <div className={styles.name}>{template.name}</div>
                    <span>
                      {new Date(template.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className={styles.action}>
                    <button
                      className={styles.restore_btn}
                      onClick={() => handleRestore(template.id)}
                    >
                      <CloudIcon /> Restore
                    </button>
                    <DropdownButton
                      id={`template-actions-${template.id}`}
                      icon={<ExtraActions />}
                      className={styles.temp_item_dropdown}
                      isOpen={openMenuId === template.id}
                      onToggle={open =>
                        setOpenMenuId(open ? template.id : null)
                      }
                    >
                      <ExtraActionsMenu
                        props={{ className: styles.extraActionsMenu }}
                        content={[
                          {
                            icon: <RepeatIcon />,
                            label: "Rewrite",
                            type: "update",
                            onClick: () => handleRewrite(template.id),
                          },
                          {
                            icon: <DeleteIcon />,
                            label: "Delete",
                            type: "delete",
                            onClick: () => handleDelete(template.id),
                          },
                        ]}
                      />
                    </DropdownButton>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <p>No templates found</p>
          ))}
      </div>
    </div>
  );
};
